import React, { useCallback, useMemo, useState } from 'react';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Spinners from '../../../components/spinner';
import {
  DynamicTable,
  useDynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import BrokerTradeHistoryHeader from '../../../constants/table-headers/brokerTradeHistoryHeader';
import { axiosInstance } from '../../../services/apiInterceptor';
import {
  useBoolean,
  useAdvanceBoolean,
  useGetCsTransactionHistory,
  useCancelCsOrder,
} from '../../../hooks';
import type { CsTransactionHistoryDto } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import { CsBrokerTradeToolSettlementService } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import VoidDoneTradeModal from './voidDoneTradeModal';
import ViewDetailsModal from './viewDetailsModal';
import AlertPopup from '../../../components/alert-popup';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';

interface TransactionHistorySectionProps {
  participantId: string | undefined;
  companyId: string | undefined;
  settlementHandleRequest: (promise: any) => Promise<any>;
  canVoid: boolean;
}

function TransactionHistorySection({
  participantId,
  companyId,
  settlementHandleRequest,
  canVoid,
}: TransactionHistorySectionProps) {
  const queryClient = useQueryClient();
  const { tableInfo } = useDynamicTable();

  const {
    schemaData: historySchemaData,
    isTableSchemaLoading,
    status: schemaStatus,
  } = useTableSchemaLoader({
    identifier: 'CS_BROKER_TRADE_HISTORY_TABLE',
  });
  const hasHistorySchema = schemaStatus === 'success' && historySchemaData?.columns?.length > 0;

  const [viewDetailsRow, setViewDetailsRow] = useState<CsTransactionHistoryDto | null>(null);
  const {
    value: isViewDetailsOpen,
    setFalse: closeViewDetails,
    setTrue: openViewDetails,
  } = useBoolean(false);
  const { value: isVoidOpen, setFalse: closeVoid, setTrue: openVoid } = useBoolean(false);
  const [voidRow, setVoidRow] = useState<CsTransactionHistoryDto | null>(null);
  const [isVoidLoading, setIsVoidLoading] = useState(false);

  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue: cancelTransactionId,
    setCustomValue: setCancelTransactionId,
  } = useAdvanceBoolean(false);
  const [alertLoading, setAlertLoading] = useState(false);

  const historySorting = tableInfo?.sorting;
  const historySkipCount = tableInfo?.skipCount ?? 0;
  const historyMaxResult = tableInfo?.maxResultCount ?? 10;

  const { data: historyData, status: historyStatus } = useGetCsTransactionHistory(
    settlementHandleRequest,
    participantId,
    companyId,
    historySorting,
    historySkipCount,
    historyMaxResult,
    {
      recordTypes: tableInfo?.recordTypes,
      fromDate: tableInfo?.fromDate,
      toDate: tableInfo?.toDate,
      cancelStatusId: tableInfo?.cancelStatusId,
      transactionStatus: tableInfo?.transactionStatus,
      settlementStatuses: tableInfo?.settlementStatus,
      transferAllocateStatuses: tableInfo?.transferAllocateOptions,
      taxCollectionStatuses: tableInfo?.taxCollectionOptions,
      awardTypes: tableInfo?.awardOptions,
    }
  );
  const historyItems = historyData?.items;
  const historyTotalCount = historyData?.totalCount ?? 0;

  const { mutate: cancelOrder } = useCancelCsOrder(settlementHandleRequest);

  const handleCancelConfirm = useCallback(() => {
    if (!cancelTransactionId) return;
    setAlertLoading(true);
    cancelOrder(
      { transactionId: cancelTransactionId, companyId: companyId || '' },
      {
        onSettled: () => {
          setAlertLoading(false);
          closeConfirmModal();
        },
      }
    );
  }, [cancelTransactionId, companyId, cancelOrder, closeConfirmModal]);

  const handleVoidConfirm = useCallback(
    async (tradeOrderId: string, reason: string) => {
      setIsVoidLoading(true);
      try {
        await settlementHandleRequest(
          CsBrokerTradeToolSettlementService.putApiAwardSettlementApiAppCsBrokerTradeToolSettlementCancel(
            tradeOrderId,
            companyId,
            { voidReason: reason }
          )
        );
        toast.success(<ToastComponent label='Success' message='Done trade voided successfully.' />);
        void queryClient.invalidateQueries('getCsTransactionHistory');
        closeVoid();
        setVoidRow(null);
      } catch {
        // handleRequest already shows error toasts
      } finally {
        setIsVoidLoading(false);
      }
    },
    [settlementHandleRequest, companyId, queryClient, closeVoid]
  );

  const openViewDetailsForRow = useCallback(
    (row: CsTransactionHistoryDto) => {
      setViewDetailsRow(row);
      openViewDetails();
    },
    [openViewDetails]
  );

  const getHistoryActions = useCallback(
    (h: CsTransactionHistoryDto) => {
      const actions: any[] = [
        {
          id: 'viewDetails',
          action: 'View Details',
          onClick: () => {
            openViewDetailsForRow(h);
          },
        },
      ];

      if (
        (h.recordTypeSystem === 'LongShareSell' || h.recordTypeSystem === 'OptionIntent') &&
        h.transactionStatus !== 'Cancelled'
      ) {
        actions.push({
          id: 'cancel',
          action: 'Cancel',
          onClick: () => {
            setCancelTransactionId(h.transactionId);
            openConfirmModal();
          },
        });
      }

      // if (isDoneTrade && isExecuted && !isAlreadyVoidedOrCancelled && canVoid) {
      //   actions.push({
      //     id: 'void',
      //     action: 'Void',
      //     onClick: () => { setVoidRow(h); openVoid(); },
      //   });
      // }

      return actions;
    },
    [openViewDetailsForRow, setCancelTransactionId, openConfirmModal, canVoid, openVoid]
  );

  const historySchema = useMemo(
    () => ({
      columns: BrokerTradeHistoryHeader({
        actions: historySchemaData?.columns || [],
        getHistoryActions,
      }),
      options: historySchemaData?.options as any,
    }),
    [historySchemaData, getHistoryActions]
  );

  const historyTableData = useMemo(() => historyItems || [], [historyItems]);

  return (
    <>
      <div className='section-card'>
        <div className='section-title'>Transaction History</div>
        {isTableSchemaLoading ? (
          <Spinners id='historySchemaSpinner' />
        ) : !hasHistorySchema ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
            Table schema not available. Please seed the{' '}
            <strong>CS_BROKER_TRADE_HISTORY_TABLE</strong> schema in the Administration Service.
          </div>
        ) : (
          <DynamicTable
            id='brokerTradeHistoryTable'
            schema={historySchema}
            data={historyTableData}
            totalCounts={historyTotalCount || 10}
            loading={historyStatus}
            showPagination={historyTotalCount > 0}
            showPaginationList={historyTotalCount > historyMaxResult}
            components={{}}
          />
        )}
      </div>

      {/* Cancel Confirmation */}
      <AnimationWrapper>
        {isConfirmOpen && (
          <div id='cancelConfirmModal'>
            <AlertPopup
              isShow={isConfirmOpen}
              handleSubmit={handleCancelConfirm}
              closeModal={closeConfirmModal}
              customTitle='Are you sure you want to cancel this award transaction?'
              loading={alertLoading}
            />
          </div>
        )}
      </AnimationWrapper>

      {/* View Details Modal */}
      <AnimationWrapper>
        {isViewDetailsOpen && (
          <ViewDetailsModal
            isOpen={isViewDetailsOpen}
            onClose={() => {
              closeViewDetails();
              setViewDetailsRow(null);
            }}
            row={viewDetailsRow}
            companyId={companyId || ''}
          />
        )}
      </AnimationWrapper>

      {/* Void Done Trade Modal */}
      <AnimationWrapper>
        {isVoidOpen && (
          <VoidDoneTradeModal
            isOpen={isVoidOpen}
            onClose={() => {
              closeVoid();
              setVoidRow(null);
            }}
            row={voidRow}
            onConfirmVoid={handleVoidConfirm}
            isLoading={isVoidLoading}
          />
        )}
      </AnimationWrapper>
    </>
  );
}

export default withDynamicTableContext(TransactionHistorySection, true, axiosInstance);
